/**
 * Relay-side Google Workspace calendar connection hooks (#1881).
 *
 * Lifecycle (mirrors lib/m365.ts, user-scoped only):
 *   1. useStartGoogleConnect() — POST relay-google-auth-start → authorization_url; redirect.
 *   2. /auth/callback/google_calendar → useCompleteGoogleCallback() → relay-google-auth-callback.
 *   3. useGoogleConnection() — reads the integration row for status.
 *   4. useDisconnectGoogle() — soft-deletes token + integration rows (RLS).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type GoogleConnectionStatus = "connected" | "disconnected" | "reauth_required";

export interface GoogleConnection {
  status: GoogleConnectionStatus;
  connected_at: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  sync_role: string | null;
  last_polled_at: string | null;
}

const EMPTY_CONNECTION: GoogleConnection = {
  status: "disconnected",
  connected_at: null,
  last_sync_at: null,
  last_sync_status: null,
  sync_role: null,
  last_polled_at: null,
};

export function useGoogleConnection(organizationId: string | null, userId: string | null) {
  const query = useQuery({
    queryKey: ["google-connection", organizationId, userId ?? null],
    queryFn: async (): Promise<GoogleConnection> => {
      if (!organizationId || !userId) return EMPTY_CONNECTION;
      const { data, error } = await supabase
        .schema("public")
        .from("calendar_integrations")
        .select("status, connected_at, last_sync_at, last_sync_status, sync_role, last_polled_at")
        .eq("organization_id", organizationId)
        .eq("user_id", userId)
        .eq("provider", "google_calendar")
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      if (!data) return EMPTY_CONNECTION;
      const row = data as {
        status: string | null;
        connected_at: string | null;
        last_sync_at: string | null;
        last_sync_status: string | null;
        sync_role: string | null;
        last_polled_at: string | null;
      };
      return {
        status: (row.status as GoogleConnectionStatus) ?? "disconnected",
        connected_at: row.connected_at ?? null,
        last_sync_at: row.last_sync_at ?? null,
        last_sync_status: row.last_sync_status ?? null,
        sync_role: row.sync_role ?? null,
        last_polled_at: row.last_polled_at ?? null,
      };
    },
    enabled: !!organizationId && !!userId,
    staleTime: 30_000,
  });
  return { connection: query.data ?? EMPTY_CONNECTION, isLoading: query.isLoading };
}

export function useStartGoogleConnect(organizationId: string | null) {
  return useMutation({
    mutationFn: async (): Promise<{ authorization_url: string }> => {
      if (!organizationId) throw new Error("No active organization");
      const redirect_uri = `${window.location.origin}/auth/callback/google_calendar`;
      const { data, error } = await supabase.functions.invoke("relay-google-auth-start", {
        body: { organization_id: organizationId, redirect_uri },
      });
      if (error) throw new Error(error.message);
      return data as { authorization_url: string };
    },
  });
}

export interface CompleteGoogleCallbackArgs {
  code: string;
  state: string;
}

export function useCompleteGoogleCallback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: CompleteGoogleCallbackArgs) => {
      const redirect_uri = `${window.location.origin}/auth/callback/google_calendar`;
      const { data, error } = await supabase.functions.invoke("relay-google-auth-callback", {
        body: { code: args.code, state: args.state, redirect_uri },
      });
      if (error) {
        let message = error.message;
        try {
          const ctx = (error as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) message = body.error as string;
          }
        } catch { /* keep error.message */ }
        throw new Error(message);
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["google-connection"] });
    },
  });
}

export function useDisconnectGoogle(organizationId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!organizationId || !userId) throw new Error("organizationId and userId required");
      const nowIso = new Date().toISOString();
      const { error: tokErr } = await supabase
        .schema("public")
        .from("calendar_oauth_tokens")
        .update({ deleted_at: nowIso, updated_at: nowIso })
        .eq("organization_id", organizationId)
        .eq("user_id", userId)
        .eq("provider", "google_calendar")
        .is("deleted_at", null);
      if (tokErr) throw tokErr;
      const { error: intErr } = await supabase
        .schema("public")
        .from("calendar_integrations")
        .update({ deleted_at: nowIso, updated_at: nowIso })
        .eq("organization_id", organizationId)
        .eq("user_id", userId)
        .eq("provider", "google_calendar")
        .is("deleted_at", null);
      if (intErr) throw intErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["google-connection", organizationId] });
    },
  });
}
